// All Reports-page PDF generators in one place.
// Each is a pure RENDERER: it receives a server-computed payload (from the
// canonical /api/reports/* endpoints) and lays it out with jsPDF. NO financial
// math happens here — every number is computed server-side by the canonical
// profit engine (services/profit_calculator.py). Phase B: "One Financial Truth".
//
// Naming convention: `generate{ReportType}Report(args...)`. Each saves the
// PDF directly via `doc.save()` — they don't return the doc.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';

import { formatCurrency } from './utils';
import { addFooter, BRAND_DARK, RED_500 } from './pdf/_helpers';

// ── Performance Metrics ───────────────────────────────────────────────────
// `performance` is the server payload from GET /api/reports/portfolio → .performance
export function generatePerformanceReport(performance) {
  if (!performance) {
    toast.error('No sold projects to generate metrics');
    return;
  }
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('PERFORMANCE METRICS REPORT', 14, 22);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 22, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('KEY PERFORMANCE INDICATORS', 14, 50);

  const kpiData = [
    ['Total Deals Closed', performance.total_deals],
    ['Gross Profit (before commissions)', formatCurrency(performance.total_gross_profit)],
    ['Commissions Paid', formatCurrency(performance.total_commissions)],
    ['Net Profit', formatCurrency(performance.total_net_profit)],
    ['Average Gross Profit per Deal', formatCurrency(performance.avg_profit)],
    ['Average Days to Close', `${performance.avg_days_held} days`],
    ['Average Deal Size (Purchase)', formatCurrency(performance.avg_deal_size)],
    ['Average Sale Price', formatCurrency(performance.avg_sale_price)],
    ['Average ROI', `${(performance.avg_roi || 0).toFixed(1)}%`],
    ['Win Rate (Profitable Deals)', `${(performance.win_rate || 0).toFixed(1)}%`],
  ];
  autoTable(doc, {
    startY: 55, body: kpiData, theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('YEAR-TO-DATE PERFORMANCE', 14, doc.lastAutoTable.finalY + 15);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    body: [
      ['YTD Deals Closed', performance.ytd_deals],
      ['YTD Total Profit', formatCurrency(performance.ytd_profit)],
    ],
    theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PERFORMANCE BY STRATEGY', 14, doc.lastAutoTable.finalY + 15);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Strategy', 'Count', 'Total Profit']],
    body: [
      ['Flip Deals', performance.flip_deals, formatCurrency(performance.flip_profit)],
      ['Subdivide Deals', performance.subdivide_deals, formatCurrency(performance.subdivide_profit)],
    ],
    theme: 'striped', styles: { fontSize: 10 }, margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('NOTABLE DEALS', 14, doc.lastAutoTable.finalY + 15);
  const notableData = [];
  if (performance.best_deal) notableData.push(['Best Profit', performance.best_deal.name, formatCurrency(performance.best_deal.profit)]);
  if (performance.worst_deal) notableData.push(['Lowest Profit', performance.worst_deal.name, formatCurrency(performance.worst_deal.profit)]);
  if (performance.fastest_deal) notableData.push(['Fastest Close', performance.fastest_deal.name, `${performance.fastest_deal.days_held} days`]);
  if (performance.slowest_deal) notableData.push(['Longest Hold', performance.slowest_deal.name, `${performance.slowest_deal.days_held} days`]);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Category', 'Project', 'Value']],
    body: notableData,
    theme: 'striped', styles: { fontSize: 10 }, margin: { left: 14 },
  });

  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('LandFlow', 14, 285);

  doc.save(`Performance_Metrics_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Performance metrics report downloaded');
}

// ── Notes Report ───────────────────────────────────────────────────────────
export function generateNotesReport(notesReport) {
  if (!notesReport) {
    toast.error('No notes data available');
    return;
  }
  try {
    const doc = new jsPDF();
    const { summary, ytd_stats, notes } = notesReport;
    if (!summary || !notes) {
      toast.error('Notes report data is incomplete');
      return;
    }

    doc.setFontSize(18);
    doc.setTextColor(...BRAND_DARK);
    doc.text('Owner-Financed Notes Report', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Portfolio Summary', 14, 40);

    autoTable(doc, {
      startY: 45,
      head: [['Metric', 'Value']],
      body: [
        ['Total Notes', (summary.total_notes || 0).toString()],
        ['Active Notes', (summary.active_notes || 0).toString()],
        ['Paid Off Notes', (summary.paid_off_notes || 0).toString()],
        ['Total Principal', `$${(summary.total_principal || 0).toLocaleString()}`],
        ['Total Remaining', `$${(summary.total_remaining_balance || 0).toLocaleString()}`],
        ['Total Received', `$${(summary.total_received_all_time || 0).toLocaleString()}`],
        ['Interest Received', `$${(summary.total_interest_received || 0).toLocaleString()}`],
        ['Expected Monthly', `$${(summary.expected_monthly_income || 0).toLocaleString()}`],
      ],
      theme: 'grid', headStyles: { fillColor: BRAND_DARK }, styles: { fontSize: 10 }, margin: { left: 14 },
    });

    if (ytd_stats) {
      const ytdY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text(`${ytd_stats.year} Year-to-Date`, 14, ytdY);
      autoTable(doc, {
        startY: ytdY + 5,
        head: [['Metric', 'Value']],
        body: [
          ['Payments Received', (ytd_stats.total_payments || 0).toString()],
          ['Total Amount', `$${(ytd_stats.total_amount || 0).toLocaleString()}`],
          ['Principal', `$${(ytd_stats.principal_received || 0).toLocaleString()}`],
          ['Interest', `$${(ytd_stats.interest_received || 0).toLocaleString()}`],
        ],
        theme: 'grid', headStyles: { fillColor: [198, 142, 93] }, styles: { fontSize: 10 }, margin: { left: 14 },
      });
    }

    if (notes && notes.length > 0) {
      const notesY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text('Notes Detail', 14, notesY);
      autoTable(doc, {
        startY: notesY + 5,
        head: [['Buyer', 'Property', 'Status', 'Principal', 'Rate', 'Monthly', 'Remaining', 'Progress']],
        body: notes.map(n => [
          n.buyer_name || 'Unknown',
          n.project_name || 'Unknown',
          n.status || 'Unknown',
          `$${(n.principal_amount || 0).toLocaleString()}`,
          `${((n.interest_rate || 0) * 100).toFixed(2)}%`,
          `$${(n.monthly_payment || 0).toLocaleString()}`,
          `$${(n.remaining_balance || 0).toLocaleString()}`,
          `${(((n.total_received || 0) / (n.principal_amount || 1)) * 100).toFixed(1)}%`,
        ]),
        theme: 'grid', headStyles: { fillColor: BRAND_DARK }, styles: { fontSize: 8 }, margin: { left: 14 },
      });
    }

    doc.setTextColor(128, 128, 128);
    doc.setFontSize(8);
    doc.text('LandFlow', 14, 285);

    doc.save(`Notes_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Notes report downloaded');
  } catch (error) {
    console.error('Error generating notes report:', error);
    toast.error('Failed to generate report: ' + error.message);
  }
}

// ── Dead Deals ──────────────────────────────────────────────────────────────
export function generateDeadDealsReport(deadDealsReport) {
  if (!deadDealsReport) {
    toast.error('No dead deals data available');
    return;
  }
  try {
    const doc = new jsPDF();
    const { summary, deals, trends } = deadDealsReport;
    const periodLabel = deadDealsReport.period_label || 'All Time';
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(...RED_500);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DEAD DEALS REPORT', 14, 22);
    doc.setFontSize(10);
    doc.text(`${periodLabel} | Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 22, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('LOSS SUMMARY', 14, 50);
    autoTable(doc, {
      startY: 55,
      head: [['Metric', 'Value']],
      body: [
        ['Total Dead Deals', (summary.total_dead_deals || 0).toString()],
        ['Total Out-of-Pocket Expenses', formatCurrency(summary.total_out_of_pocket || 0)],
        ['Total Refunds', formatCurrency(summary.total_refunds || 0)],
        ['Net Sunk Costs', formatCurrency(summary.total_losses || 0)],
        ['Avg Loss Per Deal', formatCurrency(summary.average_loss_per_deal || 0)],
      ],
      theme: 'grid', headStyles: { fillColor: RED_500 }, styles: { fontSize: 10 }, margin: { left: 14 },
    });

    if (trends) {
      const trendsY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('TREND ANALYSIS', 14, trendsY);

      if (trends.by_state?.length > 0) {
        autoTable(doc, {
          startY: trendsY + 5,
          head: [['State', 'Dead Deals', 'Capital Committed']],
          body: trends.by_state.map(s => [s.state, s.count, formatCurrency(s.total_price)]),
          theme: 'grid', headStyles: { fillColor: [107, 114, 128] }, styles: { fontSize: 9 }, margin: { left: 14 },
        });
      }
      if (trends.by_acreage?.length > 0) {
        const acY = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(11);
        doc.text('By Acreage Range', 14, acY);
        autoTable(doc, {
          startY: acY + 3,
          head: [['Acreage Range', 'Count']],
          body: trends.by_acreage.map(a => [a.range, a.count]),
          theme: 'grid', headStyles: { fillColor: [107, 114, 128] }, styles: { fontSize: 9 }, margin: { left: 14 },
        });
      }
      if (trends.by_price?.length > 0) {
        const prY = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(11);
        doc.text('By Price Range', 14, prY);
        autoTable(doc, {
          startY: prY + 3,
          head: [['Price Range', 'Count']],
          body: trends.by_price.map(p => [p.range, p.count]),
          theme: 'grid', headStyles: { fillColor: [107, 114, 128] }, styles: { fontSize: 9 }, margin: { left: 14 },
        });
      }
    }

    doc.addPage();
    doc.setFillColor(...RED_500);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DEAD DEALS DETAIL', 14, 17);
    doc.setTextColor(0, 0, 0);
    autoTable(doc, {
      startY: 32,
      head: [['Property', 'Location', 'Acres', 'Expenses', 'Refunds', 'Net Loss']],
      body: (deals || []).map(d => [
        d.project_name || 'Unknown',
        `${d.county || ''}, ${d.state || ''}`.trim().replace(/^,\s*/, ''),
        d.acres ? `${Number(d.acres).toFixed(2)} ac` : '-',
        formatCurrency(d.total_out_of_pocket || 0),
        formatCurrency(d.total_refunds || 0),
        formatCurrency(d.net_loss || 0),
      ]),
      theme: 'grid', headStyles: { fillColor: RED_500 }, styles: { fontSize: 8 },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
      margin: { left: 14 },
    });

    addFooter(doc);
    doc.save(`Dead_Deals_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Dead deals report downloaded');
  } catch (error) {
    console.error('Error generating dead deals report:', error);
    toast.error('Failed to generate report: ' + error.message);
  }
}

// ── Portfolio Summary ──────────────────────────────────────────────────────
// `report` is the server payload from GET /api/reports/portfolio
export function generatePortfolioReport(fileName, report) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const { overview, pnl, status_counts: statusCounts, projects, filters } = report;

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('PORTFOLIO SUMMARY REPORT', 14, 22);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 22, { align: 'right' });
  doc.text(`Date Range: ${filters?.date_range_label || 'All Time'}`, pageWidth - 14, 30, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PORTFOLIO OVERVIEW', 14, 50);

  autoTable(doc, {
    startY: 55,
    body: [
      ['Total Projects', `${overview.total_projects}`],
      ['Active Projects', `${overview.active_projects}`],
      ['Sold/Closed Projects', `${overview.sold_projects}`],
      ['Total Purchase Value', formatCurrency(overview.total_purchase_value)],
      ['Total Sales Revenue', formatCurrency(overview.total_sales_revenue)],
    ],
    theme: 'plain', styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  if (overview.sold_projects > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PROFIT & LOSS SUMMARY (Closed Deals)', 14, doc.lastAutoTable.finalY + 15);

    const pnlData = [
      ['Total Sales Revenue', formatCurrency(pnl.total_sales_revenue)],
      ['Less: Cost of Sales (Purchase Prices)', `(${formatCurrency(pnl.cost_of_sales)})`],
      ['GROSS PROFIT', formatCurrency(pnl.gross_profit)],
      ['', ''],
      ['Less: Operating Expenses', `(${formatCurrency(pnl.operating_expenses)})`],
      ['Plus: Reimbursements', formatCurrency(pnl.reimbursements)],
      ['OPERATING PROFIT', formatCurrency(pnl.operating_profit)],
      ['', ''],
      ['Less: Commissions', `(${formatCurrency(pnl.commissions)})`],
      ['NET PROFIT', formatCurrency(pnl.net_profit)],
    ];
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      body: pnlData,
      theme: 'plain', styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70 }, 1: { cellWidth: 45, halign: 'right', fontStyle: 'bold' } },
      didParseCell: (data) => {
        const label = data.row.raw?.[0] || '';
        if (['GROSS PROFIT', 'OPERATING PROFIT', 'NET PROFIT'].includes(label)) {
          data.cell.styles.fillColor = [245, 245, 240];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      margin: { left: 14 },
    });
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECTS BY STATUS', 14, doc.lastAutoTable.finalY + 15);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Status', 'Count']],
    body: Object.entries(statusCounts || {}).map(([s, c]) => [s.replace('_', ' '), c]),
    theme: 'striped', styles: { fontSize: 10 }, headStyles: { fillColor: BRAND_DARK }, margin: { left: 14 },
  });

  // Project list page
  doc.addPage();
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 25, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT LIST', 14, 17);
  doc.setTextColor(0, 0, 0);

  autoTable(doc, {
    startY: 32,
    head: [['Project', 'Status', 'Purchase', 'Sale', 'Expenses', 'Profit', 'Acres']],
    body: (projects || []).map(p => [
      p.name,
      (p.status || '').replace('_', ' '),
      formatCurrency(p.purchase_price),
      p.sale_price ? formatCurrency(p.sale_price) : '-',
      p.expenses > 0 ? formatCurrency(p.expenses) : '-',
      p.profit !== null && p.profit !== undefined ? formatCurrency(p.profit) : '-',
      p.acres ? `${Number(p.acres).toFixed(2)} ac` : '-',
    ]),
    theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: BRAND_DARK },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' }, 6: { halign: 'right' } },
    margin: { left: 14 },
  });

  addFooter(doc);
  doc.save(`${fileName}.pdf`);
  toast.success('Portfolio report downloaded');
}

// ── Capital Breakdown ─────────────────────────────────────────────────────
// `report` is the server payload from GET /api/reports/capital
export function generateCapitalBreakdownReport(fileName, report) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const { by_party: party, expenses_by_category: categories, by_project: byProject,
          by_project_totals: totals, recent_transactions: recent, filters } = report;

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CAPITAL DEPLOYMENT REPORT', 14, 22);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 22, { align: 'right' });
  doc.text(`Date Range: ${filters?.date_range_label || 'All Time'}`, pageWidth - 14, 30, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CAPITAL BY PARTY', 14, 50);
  autoTable(doc, {
    startY: 55,
    head: [['', 'Investor/Partner', 'Land Finder', 'Shared/Unassigned', 'Total']],
    body: [
      ['JV Capital', formatCurrency(party.total_jv_capital), '-', '-', formatCurrency(party.total_jv_capital)],
      ['Purchase Price', formatCurrency(party.investor_purchase), formatCurrency(party.land_finder_purchase), '-', formatCurrency(party.investor_purchase + party.land_finder_purchase)],
      ['EMD', formatCurrency(party.investor_emd), formatCurrency(party.land_finder_emd), '-', formatCurrency(party.investor_emd + party.land_finder_emd)],
      ['Expenses', formatCurrency(party.investor_expenses), formatCurrency(party.land_finder_expenses), formatCurrency(party.shared_expenses), formatCurrency(party.investor_expenses + party.land_finder_expenses + party.shared_expenses)],
      ['Less: Reimbursements', `(${formatCurrency(party.investor_reimbursements)})`, `(${formatCurrency(party.land_finder_reimbursements)})`, `(${formatCurrency(party.shared_reimbursements)})`, `(${formatCurrency(party.investor_reimbursements + party.land_finder_reimbursements + party.shared_reimbursements)})`],
      ['NET COST BASIS', formatCurrency(party.investor_net_cost_basis), formatCurrency(party.land_finder_net_cost_basis), formatCurrency(party.shared_net), formatCurrency(party.total_net_cost_basis)],
    ],
    theme: 'striped', styles: { fontSize: 9 }, headStyles: { fillColor: BRAND_DARK },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { halign: 'right', cellWidth: 35 },
      2: { halign: 'right', cellWidth: 30 },
      3: { halign: 'right', cellWidth: 35 },
      4: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
    },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPENSES BY CATEGORY', 14, doc.lastAutoTable.finalY + 15);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Category', 'Amount', '% of Total']],
    body: (categories || []).map(item => [
      item.name,
      formatCurrency(item.value),
      `${item.pct}%`,
    ]),
    theme: 'striped', styles: { fontSize: 9 }, headStyles: { fillColor: BRAND_DARK },
    columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40, halign: 'right' }, 2: { cellWidth: 30, halign: 'right' } },
    margin: { left: 14 },
  });

  // Capital by Project
  doc.addPage();
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 25, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CAPITAL BY PROJECT', 14, 17);
  doc.setTextColor(0, 0, 0);

  const projectCapitalRows = (byProject || []).map(p => [
    p.name,
    (p.status || '').replace('_', ' '),
    formatCurrency(p.purchase_price),
    formatCurrency(p.emd),
    formatCurrency(p.expenses),
    p.reimbursements > 0 ? `(${formatCurrency(p.reimbursements)})` : '-',
    formatCurrency(p.net_cost_basis),
  ]);
  if (totals) {
    projectCapitalRows.push([
      'TOTAL', '',
      formatCurrency(totals.purchase_price),
      formatCurrency(totals.emd),
      formatCurrency(totals.expenses),
      totals.reimbursements > 0 ? `(${formatCurrency(totals.reimbursements)})` : '-',
      formatCurrency(totals.net_cost_basis),
    ]);
  }

  autoTable(doc, {
    startY: 32,
    head: [['Project', 'Status', 'Purchase', 'EMD', 'Expenses', 'Reimb.', 'Net Cost Basis']],
    body: projectCapitalRows,
    theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: BRAND_DARK },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
    didParseCell: (data) => {
      if (totals && data.row.index === projectCapitalRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [245, 245, 240];
      }
    },
    margin: { left: 14 },
  });

  // Recent transactions (if there's room)
  if (doc.lastAutoTable.finalY < 200 && recent?.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RECENT TRANSACTIONS', 14, doc.lastAutoTable.finalY + 15);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Date', 'Project', 'Type', 'Party', 'Amount']],
      body: recent.map(t => [
        t.date || '-',
        (t.project_name || 'Unknown').substring(0, 20),
        t.type,
        t.party,
        formatCurrency(t.amount),
      ]),
      theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: BRAND_DARK },
      columnStyles: { 4: { halign: 'right' } },
      margin: { left: 14 },
    });
  }

  addFooter(doc);
  doc.save(`${fileName}.pdf`);
  toast.success('Capital breakdown report downloaded');
}

// ── Commission ─────────────────────────────────────────────────────────────
export function generateCommissionReport(fileName, ledgerSummary) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION REPORT', 14, 22);
  doc.setFontSize(10);
  doc.text(`Year: ${new Date().getFullYear()}`, pageWidth - 14, 22, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION SUMMARY', 14, 50);
  autoTable(doc, {
    startY: 55,
    body: [
      ['YTD Commissions', formatCurrency(ledgerSummary?.ytd_commissions || 0)],
      ['YTD Draws', formatCurrency(ledgerSummary?.ytd_draws || 0)],
      ['YTD Payouts', formatCurrency(ledgerSummary?.ytd_payouts || 0)],
      ['Current Balance', formatCurrency(ledgerSummary?.current_balance || 0)],
      ['Tier 1 Remaining', formatCurrency(ledgerSummary?.tier_1_remaining || 0)],
    ],
    theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMISSION STRUCTURE', 14, doc.lastAutoTable.finalY + 20);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 25,
    head: [['Tier', 'Rate']],
    body: [['Tier 1 (First $100K)', '10%'], ['Tier 2 (After $100K)', '5%']],
    theme: 'striped', styles: { fontSize: 10 }, margin: { left: 14 },
  });

  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('LandFlow', 14, 285);

  doc.save(`${fileName}.pdf`);
  toast.success('Commission report downloaded');
}

// ── JV Summary ─────────────────────────────────────────────────────────────
// `jvSummary` is the server payload from GET /api/reports/portfolio → .jv_summary
export function generateJVSummaryReport(fileName, jvSummary, dateRangeText) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('JV AGREEMENTS SUMMARY', 14, 22);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 22, { align: 'right' });
  doc.text(`Date Range: ${dateRangeText || 'All Time'}`, pageWidth - 14, 30, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('OVERVIEW', 14, 50);
  autoTable(doc, {
    startY: 55,
    body: [
      ['Total JV Agreements', jvSummary.total_agreements],
      ['Unique Investors', jvSummary.unique_investors],
      ['Total Capital Contributed', formatCurrency(jvSummary.total_capital_contributed)],
    ],
    theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('JV AGREEMENTS', 14, doc.lastAutoTable.finalY + 20);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 25,
    head: [['Project', 'Investor', 'Capital', 'Tier 1 Split']],
    body: (jvSummary.agreements || []).map(jv => [
      jv.project_name || 'Unknown',
      jv.investor_name,
      formatCurrency(jv.capital_contributed),
      `${((jv.tier_1_investor_split || 0) * 100).toFixed(0)}%`,
    ]),
    theme: 'striped', styles: { fontSize: 9 }, margin: { left: 14 },
  });

  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('LandFlow', 14, 285);

  doc.save(`${fileName}.pdf`);
  toast.success('JV summary report downloaded');
}

// ── Subdivision Financial ──────────────────────────────────────────────────
export function generateSubdivisionReport(subdivisionFinancials) {
  if (!subdivisionFinancials) return;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const data = subdivisionFinancials;

  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('SUBDIVISION FINANCIAL REPORT', 14, 18);
  doc.setFontSize(12);
  doc.text(data.parent_project.name, 14, 28);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 18, { align: 'right' });
  doc.text(`Status: ${data.completion_status.percent_complete}% Complete`, pageWidth - 14, 28, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PARENT LOT OVERVIEW', 14, 55);
  autoTable(doc, {
    startY: 60,
    body: [
      ['Total Purchase Price', formatCurrency(data.parent_project.total_purchase_price)],
      ['Acquisition Date', data.parent_project.acquisition_date || 'N/A'],
      ['Total Lots Created', data.totals.total_lots],
      ['Lots Sold', data.totals.sold_lots],
      ['Lots Remaining', data.totals.remaining_lots],
    ],
    theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  if (data.jv_summary.has_jv) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('JV INVESTOR SUMMARY', 14, doc.lastAutoTable.finalY + 15);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      body: [
        ['Investor(s)', data.jv_summary.investors.map(i => i.name).join(', ')],
        ['Total JV Capital', formatCurrency(data.jv_summary.total_jv_capital)],
        ['Investor Cost Basis', formatCurrency(data.jv_summary.investor_cost_basis)],
        ['Active Tier', data.jv_summary.active_tier ? `Tier ${data.jv_summary.active_tier} (${data.jv_summary.investor_split_percentage}%)` : 'N/A'],
        ['Investor Profit', formatCurrency(data.jv_summary.investor_profit)],
        ['Investor Total Return', formatCurrency(data.jv_summary.investor_total_return)],
      ],
      theme: 'striped', styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 60, halign: 'right' } },
      margin: { left: 14 },
    });
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CHILD LOTS DETAIL', 14, doc.lastAutoTable.finalY + 15);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Lot', 'Status', 'Cost %', 'Cost Basis', 'Sale Price', 'Gross Profit']],
    body: data.children_summary.map(child => [
      child.name,
      child.status.replace('_', ' '),
      `${child.cost_basis_percentage}%`,
      formatCurrency(child.allocated_cost_basis),
      child.is_sold ? formatCurrency(child.sale_price) : '-',
      child.is_sold ? formatCurrency(child.sale_price - child.allocated_cost_basis) : '-',
    ]),
    theme: 'striped', styles: { fontSize: 9 },
    columnStyles: { 2: { halign: 'center' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CONSOLIDATED FINANCIALS', 14, doc.lastAutoTable.finalY + 15);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    body: [
      ['Total Sale Revenue', formatCurrency(data.totals.total_sale_price)],
      ['Total Expenses', formatCurrency(data.totals.total_expenses)],
      ['Gross Profit', formatCurrency(data.totals.gross_profit)],
      ['Investor Profit Share', formatCurrency(data.jv_summary.investor_profit)],
      ['Land Finder Profit Share', formatCurrency(data.land_finder_summary.land_finder_profit)],
    ],
    theme: 'striped', styles: { fontSize: 10 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 50, halign: 'right' } },
    margin: { left: 14 },
  });

  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('LandFlow', 14, 285);

  doc.save(`Subdivision_${data.parent_project.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Subdivision report downloaded');
}
